import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import KorvTube from "./KorvTube";
import { COLLECTION } from "../constants/testIds";

/** Spotlit product grid — KorvTube on tonal backdrops */
export default function CollectionShowcase({ products }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  // Tonal backdrops per product — each product sits in its own field
  const bgs = ["#2a2724", "#d9cfc0", "#c9baa4", "#221820", "#e0d0b6"];
  const textOn = ["light", "dark", "dark", "light", "dark"];

  return (
    <section ref={ref} className="relative bg-ink text-bone py-32 md:py-44 overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-16 md:mb-24">
          <div>
            <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">The Collection · Volume I</p>
            <h2 className="font-serif-display text-5xl md:text-8xl leading-[0.95] max-w-[14ch]">
              Five compositions. <span className="italic font-serif-italic">One</span> intention.
            </h2>
          </div>
          <Link to="/collection" className="hidden md:inline-flex editorial-link text-[11px] uppercase tracking-editorial !text-bone !border-bone/60">
            View all →
          </Link>
        </div>
      </div>

      <motion.div style={{ y }} className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div data-testid="collection-showcase" className="grid grid-cols-12 gap-6 md:gap-8">
          {products.map((p, i) => (
            <ProductPortrait
              key={p.slug}
              product={p}
              index={i}
              bg={bgs[i % bgs.length]}
              onDark={textOn[i % textOn.length] === "light"}
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function ProductPortrait({ product, index, bg, onDark }) {
  const layout = [
    "col-span-12 md:col-span-5",
    "col-span-6 md:col-span-3 md:mt-24",
    "col-span-6 md:col-span-4 md:mt-8",
    "col-span-6 md:col-span-4 md:mt-40",
    "col-span-6 md:col-span-4",
  ];
  const capMap = { "#141212": "dark", "#e6ddcf": "light", "#c9a37a": "dark" };
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.1, delay: index * 0.08, ease: [0.2, 0.7, 0.1, 1] }}
      className={layout[index % layout.length]}
    >
      <Link
        to={`/collection/${product.slug}`}
        data-testid={COLLECTION.item(product.slug)}
        className="group block"
      >
        <div className="relative overflow-hidden aspect-[3/4]" style={{ background: bg }}>
          {/* Ambient light */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background: "radial-gradient(58% 55% at 50% 42%, rgba(255,255,255,0.16), transparent 65%)",
            }}
          />
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.1, 1] }}
          >
            <KorvTube
              color={product.swatch_hex}
              cap={capMap[product.swatch_hex] || "same"}
              sublabel={product.category.toUpperCase()}
              animate={false}
              className="h-[78%] w-auto drop-shadow-[0_28px_36px_rgba(20,18,17,0.35)]"
            />
          </motion.div>

          <div className={`absolute left-4 top-4 text-[10px] uppercase tracking-editorial ${onDark ? "text-bone/85" : "text-ink/75"}`}>
            N° 00{index + 1}
          </div>
          <div className={`absolute right-4 top-4 flex items-center gap-2 text-[10px] uppercase tracking-editorial ${onDark ? "text-bone/85" : "text-ink/75"}`}>
            <span className="swatch-dot" style={{ background: product.swatch_hex }} />
            {product.category}
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between">
            <div>
              <p className={`font-serif-display text-4xl md:text-5xl ${onDark ? "text-bone" : "text-ink"}`}>{product.name}</p>
              <p className={`text-[11px] uppercase tracking-editorial mt-1 ${onDark ? "text-bone/70" : "text-ink/65"}`}>{product.tagline}</p>
            </div>
            <span className={`text-[11px] uppercase tracking-editorial ${onDark ? "text-bone/70" : "text-ink/65"}`}>{product.price_display}</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
